import React, { useCallback, useRef, useState } from 'react';
import { type InputBaseComponentProps } from '@mui/material/InputBase';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { IMaskInput } from 'react-imask';

export type PhoneMaskEventTarget = {
	value: string;
	unmaskedValue: string;
};

export type PhoneMaskEvent = {
	target: PhoneMaskEventTarget;
};

export type PhoneMaskProps = Omit<InputBaseComponentProps, 'onChange'> & {
	onChange?: (event: PhoneMaskEvent) => void;
	showDigitMask?: boolean;
};

const PhoneMask = React.forwardRef<HTMLInputElement, PhoneMaskProps>(
	function PhoneMask(
		{
			onChange,
			showDigitMask = false,
			defaultValue,
			value: controlledValue,
			...inputProps
		},
		ref
	) {
		const [value, setValue] = useState(defaultValue ?? '');
		const previousUnmaskedValueRef = useRef('');

		const handleAccept = useCallback(
			(maskedValue: string, { unmaskedValue }: { unmaskedValue: string }) => {
				const target: PhoneMaskEventTarget = {
					value: maskedValue,
					unmaskedValue,
				};

				// Update internal state for uncontrolled mode
				if (controlledValue === undefined) {
					setValue(maskedValue);
				}

				// Always call onChange for controlled components
				if (controlledValue !== undefined) {
					onChange?.({ target });
					return;
				}

				// For uncontrolled mode, only call onChange on valid final values
				const isValidFinalLength =
					unmaskedValue.length === 0 || unmaskedValue.length === 10;

				if (
					isValidFinalLength &&
					previousUnmaskedValueRef.current !== unmaskedValue
				) {
					onChange?.({ target });
					previousUnmaskedValueRef.current = unmaskedValue;
				}
			},
			[onChange, controlledValue]
		);

		/**
		 * This is really only here to prevent warning logs, since it breaks
		 * convention. Instead, we set the input value within the onAccept handler
		 */
		const handleChange = useCallback((): void => {
			return;
		}, []);

		return (
			<IMaskInput
				{...inputProps}
				data-testid="phone-input"
				value={controlledValue ?? value}
				mask="(000) 000-0000"
				minLength={10}
				inputRef={ref}
				onAccept={handleAccept}
				onChange={handleChange}
				lazy={!showDigitMask}
				overwrite
			/>
		);
	}
);

export type PhoneInputProps = Omit<TextFieldProps, 'onChange'> &
	Pick<PhoneMaskProps, 'onChange'>;

export function PhoneInput({
	onChange,
	...textFieldProps
}: PhoneInputProps): React.JSX.Element {
	const { InputProps, defaultValue, value, ...otherTextFieldProps } =
		textFieldProps;

	return (
		<TextField
			{...otherTextFieldProps}
			onChange={onChange as TextFieldProps['onChange']}
			type="search"
			InputProps={{
				...InputProps,
				inputComponent: PhoneMask,
				defaultValue,
				value,
			}}
		/>
	);
}
